export const SLACK_SCOPE_LABELS = {
  'app_mentions:read': 'Receive mentions of Confidence',
  'assistant:write': 'Show assistant reply progress',
  'channels:history': 'Read public channel messages',
  'channels:read': 'Read public channel information',
  'chat:write': 'Send messages',
  'chat:write.public': 'Post in public channels without joining',
  'files:read': 'Read shared files and images',
  'groups:history': 'Read private channel messages',
  'groups:read': 'Read private channel information',
  'im:history': 'Receive and read direct messages',
  'im:read': 'Identify direct conversations',
  'im:write': 'Start direct messages',
  'links:read': 'Receive shared Confidence links',
  'links:write': 'Show Confidence link previews',
  'mpim:read': 'Read group direct message information',
  'reactions:read': 'Collect emoji feedback on bot replies',
  'reactions:write': 'Add emoji reactions',
  'users:read': 'Read Slack user profiles',
  'users:read.email': 'Match Slack users to Confidence identities',
} as const;

type SlackScope = keyof typeof SLACK_SCOPE_LABELS;

const NOTIFICATION_SCOPES: SlackScope[] = ['chat:write', 'im:write'];
const BOT_IDENTITY_SCOPES: SlackScope[] = [
  'chat:write',
  'users:read',
  'users:read.email',
];

type SlackFeature = {
  name: string;
  description: string;
  scopes: SlackScope[];
};

const SLACK_FEATURES: SlackFeature[] = [
  {
    name: 'Notifications',
    description:
      'Receive configured notifications in direct messages and channels Confidence can access.',
    scopes: NOTIFICATION_SCOPES,
  },
  {
    name: 'Questions in channels',
    description:
      'Mention @Confidence in a channel it has joined to ask a question.',
    scopes: [...BOT_IDENTITY_SCOPES, 'app_mentions:read'],
  },
  {
    name: 'Questions in direct messages',
    description: 'Ask Confidence questions in a one-to-one conversation.',
    scopes: [...BOT_IDENTITY_SCOPES, 'im:write', 'im:read', 'im:history'],
  },
  {
    name: 'Public channel context',
    description:
      'Use messages and channel information from public channels Confidence has joined.',
    scopes: [
      ...BOT_IDENTITY_SCOPES,
      'app_mentions:read',
      'channels:history',
      'channels:read',
    ],
  },
  {
    name: 'Private channel context',
    description:
      'Use messages and channel information from private channels Confidence has joined.',
    scopes: [
      ...BOT_IDENTITY_SCOPES,
      'app_mentions:read',
      'groups:history',
      'groups:read',
    ],
  },
  {
    name: 'Confidence link previews',
    description:
      'Show details of Confidence resources when their links are shared in Slack.',
    scopes: [...BOT_IDENTITY_SCOPES, 'links:read', 'links:write'],
  },
  {
    name: 'Emoji feedback',
    description: 'Collect emoji reactions as feedback on Confidence replies.',
    scopes: ['reactions:read'],
  },
];

export type SlackFeatureAvailability = Pick<
  SlackFeature,
  'name' | 'description'
> & {
  available: boolean;
};

export const getSlackFeatures = (
  missingScopes: SlackScope[],
): SlackFeatureAvailability[] => {
  const hasScopes = (scopes: SlackScope[]) =>
    scopes.every(scope => !missingScopes.includes(scope));
  const canAskQuestions =
    hasScopes([...BOT_IDENTITY_SCOPES, 'app_mentions:read']) ||
    hasScopes([...BOT_IDENTITY_SCOPES, 'im:write', 'im:read', 'im:history']);
  return [
    ...SLACK_FEATURES.map(feature => ({
      ...feature,
      available: hasScopes(feature.scopes),
    })),
    {
      name: 'Images in conversations',
      description: 'Include shared images when asking Confidence a question.',
      available: canAskQuestions && hasScopes(['files:read']),
    },
    {
      name: 'Reply progress',
      description: 'See the assistant’s status while it prepares a reply.',
      available: canAskQuestions && hasScopes(['assistant:write']),
    },
  ];
};

const FAILURE_COPY = {
  missing_scopes: {
    title: 'Slack needs additional permissions',
    description:
      'The connection was not updated because Slack did not grant the permissions required for this connection.',
  },
  invalid_session: {
    title: 'This connection session is no longer valid',
    description:
      'The session expired, was already used, or could not be matched to this browser. Start a new connection flow and complete it in the same browser.',
  },
  invalid_organization: {
    title: 'Choose your Confidence account',
    description:
      'Start Connect Slack from the integrations page of the Confidence account you want to connect.',
  },
  slack_denied: {
    title: 'Slack connection was not completed',
    description:
      'Slack authorization was cancelled or denied. You can start again when you are ready.',
  },
  confidence_denied: {
    title: 'Confidence authorization was not completed',
    description:
      'Authorization was cancelled, denied, or the selected account did not match. Sign in to the intended Confidence account with permission to configure notification channels, then reconnect Slack.',
  },
  verification_unavailable: {
    title: 'Could not verify Slack permissions',
    description:
      'Slack did not provide enough information to verify the connection. The connection was not updated. Please try again later.',
  },
  slack_failed: {
    title: 'Could not connect to Slack',
    description:
      'We could not complete Slack authorization. The connection was not updated. Please start a new connection flow. If this keeps happening, contact Confidence support.',
  },
  save_failed: {
    title: 'Could not finish connecting Slack',
    description:
      'We could not confirm that your connection was saved. Check the Slack integration in your Confidence account before retrying. If this keeps happening, contact Confidence support.',
  },
  confidence_failed: {
    title: 'Could not complete Confidence sign-in',
    description:
      'The connection was not updated because Confidence sign-in could not be completed. Start a new connection flow. If this keeps happening, contact Confidence support.',
  },
  confidence_verification_unavailable: {
    title: 'Could not verify Confidence access',
    description:
      'We could not verify your access to this Confidence account. The connection was not updated. Please try again later.',
  },
  workspace_verification_unavailable: {
    title: 'Could not verify Slack workspace access',
    description:
      'We could not verify that the Slack app can access your connected workspace. The connection was not updated. Please try again later.',
  },
  workspace_connection_required: {
    title: 'Connect a Slack workspace first',
    description:
      'This Slack organization installation needs an existing workspace connection in Confidence. Start again and select “A workspace” in Slack. You can reconnect with an organization installation later.',
  },
  workspace_access_required: {
    title: 'Slack needs access to your connected workspace',
    description:
      'The Slack app cannot access this Confidence account’s connected workspace. Ask your Slack administrator to add the app to that workspace, then reconnect. The connection was not updated.',
  },
  workspace_conflict: {
    title: 'This account is connected to another Slack workspace',
    description:
      'The connection was not updated. Start again and select the Slack workspace already connected to this Confidence account, or connect the selected workspace to another Confidence account.',
  },
  workspace_missing: {
    title: 'Slack did not return a workspace',
    description:
      'The connection was not updated. Ask your Slack administrator to review the app’s installation and permission approvals, then reconnect.',
  },
  maintenance: {
    title: 'Slack connections are temporarily paused',
    description:
      'Existing Slack connections continue to work. Please try again shortly.',
  },
} as const;

type FailureReason = keyof typeof FAILURE_COPY;
type ResultBase = { requestedAccount?: string; missingScopes: SlackScope[] };
export type SlackInstallResult = ResultBase &
  (
    | {
        status: 'success' | 'partial';
        workspace: string;
        account: string;
      }
    | { status: 'failure'; reason: FailureReason }
  );

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isScope = (value: unknown): value is SlackScope =>
  typeof value === 'string' && Object.hasOwn(SLACK_SCOPE_LABELS, value);
const isReason = (value: unknown): value is FailureReason =>
  typeof value === 'string' && Object.hasOwn(FAILURE_COPY, value);
const isText = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= 1000;
const isAccount = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-z][a-z0-9-]{1,20}[a-z0-9]$/.test(value);

export const parseSlackInstallResult = (
  hash: string,
): SlackInstallResult | null => {
  if (hash.length > 16000) return null;
  try {
    const encoded = new URLSearchParams(hash.replace(/^#/, '')).get('result');
    if (!encoded) return null;
    const result: unknown = JSON.parse(encoded);
    if (
      !isRecord(result) ||
      result.version !== 1 ||
      !Array.isArray(result.missingScopes) ||
      !result.missingScopes.every(isScope) ||
      result.missingScopes.length > 19 ||
      (result.requestedAccount !== undefined &&
        !isAccount(result.requestedAccount))
    )
      return null;
    const base: ResultBase = {
      missingScopes: result.missingScopes,
      requestedAccount: result.requestedAccount,
    };
    if (result.status === 'failure' && isReason(result.reason)) {
      return {
        ...base,
        status: 'failure',
        reason: result.reason,
      };
    }
    if (
      !['success', 'partial'].includes(String(result.status)) ||
      !isText(result.workspace) ||
      !isAccount(result.account) ||
      NOTIFICATION_SCOPES.some(scope => base.missingScopes.includes(scope)) ||
      (result.status === 'success') !== (base.missingScopes.length === 0)
    )
      return null;
    return {
      ...base,
      status: result.status === 'success' ? 'success' : 'partial',
      workspace: result.workspace,
      account: result.account,
    };
  } catch {
    return null;
  }
};

export const getFailureCopy = (
  result: Extract<SlackInstallResult, { status: 'failure' }>,
): { title: string; description: string } => FAILURE_COPY[result.reason];
